/* eslint-disable react/destructuring-assignment */
import React, { Component } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import classNames from 'classnames';
import { Scrollbars } from 'react-custom-scrollbars';

// constants
import {
  VEHICLE_SELECTOR,
  DRAGGABLE_DESTINATION_CHARGERS,
  TSLA_HEADER_MAIN,
  SIDEBAR_WIDTH,
  VEHICLE_SELECTOR_HEIGHT,
  ROUTE_SUMMARY_HEIGHT,
} from '../../lib/Constants';

// components
import Btn from '../../components/Btn';
import { Charger } from '../../components/Directions';

// actions
import { toggleDestinationChargers } from '../../reducers/DestinationChargers/actions';

// helpers
import MapUtils from '../../lib/MapUtils';
import MapMarkers from '../../lib/MapMarkers';
import MapWindow from '../../lib/MapWindow';
import {
  meterToKM,
  formatDistance,
  localizeDistance,
} from '../../lib/ConversionUtils';
import Enliven from '../../lib/Enliven';
import { getNodeHeight } from '../../lib/Utils';

const ChargersPropTypes = {
  destinationChargers: PropTypes.arrayOf(PropTypes.object).isRequired, // Details of nearby destination chargers
  fields: PropTypes.arrayOf(PropTypes.object).isRequired, // Contains objects with information about each input field & location
  fieldsLength: PropTypes.number.isRequired, // Number of places/fields in a users trip
  labels: PropTypes.string.isRequired, // Distance away
  unit: PropTypes.shape(PropTypes.object).isRequired, // Object of metric and imperial units
  locale: PropTypes.string.isRequired, // Current user locale
};

const ChargersDefaultProps = {};

const Chargers = ({ props, handleChargerClick }) => {
  const {
    destinationChargers,
    fields,
    fieldsLength,
    label,
    unit,
    locale,
  } = props;

  const { lat, lng } = fields[fieldsLength - 1];
  const destination = MapUtils.latLng(lat, lng);

  // eslint-disable-next-line arrow-parens
  return destinationChargers.map(charger => {
    const chargerDetails = MapUtils.latLng(
      charger.geoPoint.lat,
      charger.geoPoint.lon,
    );

    const distance = localizeDistance(
      meterToKM(MapUtils.distanceBetweenPlots(destination, chargerDetails)),
      unit,
      'metric',
    );

    return (
      <Charger
        key={charger.id}
        title={charger.destinationChargerSite.nativeSiteName}
        duration={formatDistance(distance, unit, locale)}
        label={label}
        icon="destination-charger"
        handleClick={() => handleChargerClick(charger)}
        type="destination"
      />
    );
  });
};

Chargers.propTypes = ChargersPropTypes;

Chargers.defaultProps = ChargersDefaultProps;

const DraggablePropTypes = {
  handleChargerClick: PropTypes.func.isRequired, // Callback method passed to component
};

const DraggableDefaultProps = {};

const Draggable = props => (
  <div>
    <div className="icon-container">
      <i className="tsla-icon-angle_up" />
    </div>
    <ol className={classNames('directions destination-chargers__directions')}>
      {Chargers({ props, handleChargerClick: props.handleChargerClick }).map(
        node => node,
      )}
      <li className="h100" />
    </ol>
  </div>
);

Draggable.propTypes = DraggablePropTypes;

Draggable.defaultProps = DraggableDefaultProps;

const ScrollablePropTypes = {
  handleChargerClick: PropTypes.func.isRequired, // Callback method passed to component
  scrollableHeight: PropTypes.number.isRequired, // Calculated height for the component
};

const ScrollableDefaultProps = {};

// eslint-disable-next-line arrow-parens
const Scrollable = props => {
  const styles = {
    height: props.scrollableHeight,
    zIndex: 1,
    width: `${SIDEBAR_WIDTH}px`,
  };

  return (
    <Scrollbars style={styles} className="scrollbars">
      <ol className={classNames('directions destination-chargers__directions')}>
        {Chargers({ props, handleChargerClick: props.handleChargerClick }).map(
          node => node,
        )}
        <li className="h100" />
      </ol>
    </Scrollbars>
  );
};

Scrollable.propTypes = ScrollablePropTypes;

Scrollable.defaultProps = ScrollableDefaultProps;

const DestinationChargersPropTypes = {
  showDestinationChargers: PropTypes.bool.isRequired, // True if destination chargers are displayed
  isMobile: PropTypes.bool.isRequired, // True when the app is loaded on a handheld sized device
  btnLabel: PropTypes.string.isRequired, // Label for 'Back to Route' button
  toolTipToggle: PropTypes.bool.isRequired, // State of the tooltip
  destinationChargers: PropTypes.arrayOf(PropTypes.object).isRequired, // Details of nearby destination chargers
  chargerBody: PropTypes.shape({
    // Details about destination chargers that does not exist in TRT
    singular: PropTypes.string.isRequired,
    plural: PropTypes.string.isRequired,
  }).isRequired,
  dispatchToggleDestinationChargers: PropTypes.func.isRequired, // Toggles destination charger UI
  windowHeight: PropTypes.number.isRequired, // Height of the users screen
  fields: PropTypes.arrayOf(PropTypes.object).isRequired, // Contains objects with information about each input field & location
  fieldsLength: PropTypes.number.isRequired, // Number of places/fields in a users trip
  componentHeights: PropTypes.arrayOf(PropTypes.object).isRequired, // component heights
};

const DestinationChargersDefaultProps = {};

class DestinationChargers extends Component {
  static openChargerWindow(charger) {
    // prevent charger window from opening when directions component is in initial position
    const animation = Enliven.currentAnimation[DRAGGABLE_DESTINATION_CHARGERS];
    if (animation === Enliven.SLIDE_DOWN || animation === Enliven.SLIDE_UP) {
      return;
    }

    MapWindow.handleOpenChargerWindow({
      trt_id: charger.id,
      location: {
        lat: charger.geoPoint.lat,
        lng: charger.geoPoint.lon,
      },
    });
  }

  constructor(props) {
    super(props);

    this.toggleChargerList = this.toggleChargerList.bind(this);

    this.state = {
      markers: false,
      destinationChargerListener: false,
    };
  }

  componentDidUpdate() {
    const { destinationChargers, showDestinationChargers } = this.props;
    const { markers, destinationChargerListener } = this.state;

    if (!showDestinationChargers && markers) {
      this.unmountDestinationChargers();
    }

    if (destinationChargers.length > 0 && !markers && showDestinationChargers) {
      this.mountDestinationChargers(markers);
    }

    if (!destinationChargerListener && destinationChargers.length > 0) {
      this.initDestinationChargerZoomListener();
    }
  }

  /**
   * @description Calculates the height of DestinationChargers component
   *
   * @return {integer} scrollable height
   */
  getDestinationScrollableHeight() {
    const { windowHeight } = this.props;
    const header = getNodeHeight(TSLA_HEADER_MAIN);
    const vehicle = getNodeHeight(VEHICLE_SELECTOR) || VEHICLE_SELECTOR_HEIGHT;
    return windowHeight - header - vehicle - ROUTE_SUMMARY_HEIGHT;
  }

  unmountDestinationChargers() {
    this.setState({ markers: false });
  }

  mountDestinationChargers() {
    this.setState({ markers: true });
  }

  initDestinationChargerZoomListener() {
    const { google, map } = window;

    this.setState({ destinationChargerListener: true });

    google.maps.event.addListener(map, 'zoom_changed', () => {
      const { destinationChargers } = this.props;
      MapMarkers.updateDestinationChargerMarkers(destinationChargers);
    });
  }

  toggleChargerList() {
    const {
      showDestinationChargers,
      destinationChargers,
      isMobile,
    } = this.props;

    this.props.dispatchToggleDestinationChargers(!showDestinationChargers);

    // permanently remove the destination charger markers
    MapMarkers.deleteMapMarkers(destinationChargers, true);
    this.setState({ markers: false });

    MapWindow.closeInfoWindow(isMobile);
    MapUtils.fitMapToPolyline();
  }

  render() {
    const {
      showDestinationChargers,
      isMobile,
      btnLabel,
      toolTipToggle,
    } = this.props;

    return (
      <div
        className={classNames('destination-chargers', {
          'slide-out': showDestinationChargers && !isMobile,
          'slide-in': !showDestinationChargers && !isMobile,
          hidden: toolTipToggle && !showDestinationChargers && !isMobile,
        })}
      >
        <Btn
          styles={classNames('destination-chargers__btn', {
            hidden: isMobile,
          })}
          handleClick={this.toggleChargerList}
        >
          <i className="tsla-icon-angle_up" />
          {btnLabel}
        </Btn>
        {!isMobile && (
          <Scrollable
            scrollableHeight={this.getDestinationScrollableHeight()}
            handleChargerClick={this.constructor.openChargerWindow}
            {...this.props}
          />
        )}
        {isMobile && (
          <Draggable
            handleChargerClick={this.constructor.openChargerWindow}
            {...this.props}
          />
        )}
      </div>
    );
  }
}

DestinationChargers.propTypes = DestinationChargersPropTypes;

DestinationChargers.defaultProps = DestinationChargersDefaultProps;

const mapStateToProps = state => ({
  showDestinationChargers: state.DestinationChargers.showDestinationChargers,
  isMobile: state.App.isMobile,
  btnLabel: state.DestinationChargers.btnLabel,
  toolTipToggle: state.Tooltip.toggle,
  directionsLoaded: state.Directions.directionsLoaded,
  destinationChargers: state.DestinationChargers.destinationChargers,
  chargerBody: state.DestinationChargers.body,
  fields: state.Places.fields,
  fieldsLength: state.Places.fieldsLength,
  label: state.DestinationChargers.label,
  locale: state.App.locale,
  unit: state.App.unit,
  windowHeight: state.App.windowHeight,
  componentHeights: state.App.componentHeights,
});

const mapDispatchToProps = dispatch => ({
  // eslint-disable-next-line arrow-parens
  dispatchToggleDestinationChargers: showDestinationChargers => {
    dispatch(toggleDestinationChargers(showDestinationChargers));
  },
});

export default connect(
  mapStateToProps,
  mapDispatchToProps,
)(DestinationChargers);
